import EmptyState from '@/components/EmptyState';
import { PaymentCountdown } from '@/features/orders/components/PaymentCountdown';
import { useAwaitingPaymentOrders } from '@/features/orders/hooks/useAwaitingPaymentOrders';
import { paymentMethodIcon, paymentMethodLabel } from '@/features/orders/utils/payment-method';
import { useT } from '@/i18n';
import { formatMoney } from '@/utils/money';
import { formatPhone } from '@/utils/phone';
import type { AwaitingPaymentOrder } from '@restaurant/api-client';
import {
  Block,
  BlockFooter,
  Link as F7Link,
  List,
  ListItem,
  NavRight,
  Navbar,
  Page,
  Preloader,
} from 'framework7-react';

export default function AwaitingPaymentPage() {
  const t = useT();
  const ap = t.orders.awaitingPayment;
  const { data, isLoading, isError, refetch, isFetching } = useAwaitingPaymentOrders();
  const orders = data?.orders ?? [];

  return (
    <Page name="orders-awaiting-payment">
      <Navbar title={ap.title} backLink={t.orders.page.title}>
        <NavRight>
          <F7Link
            iconF7="arrow_clockwise"
            tooltip={ap.refresh}
            aria-label={ap.refresh}
            onClick={() => {
              if (!isFetching) refetch();
            }}
          />
        </NavRight>
      </Navbar>

      {isLoading && (
        <Block style={{ textAlign: 'center', marginTop: 40 }}>
          <Preloader />
        </Block>
      )}

      {!isLoading && isError && <EmptyState icon="exclamationmark_triangle" title={ap.loadError} />}

      {!isLoading && !isError && orders.length === 0 && (
        <EmptyState icon="hourglass" title={ap.empty} description={ap.emptyDescription} />
      )}

      {!isLoading && !isError && orders.length > 0 && (
        <>
          <List
            strong
            inset
            dividers
            mediaList
            style={{ '--f7-list-item-text-max-lines': 99 } as React.CSSProperties}
          >
            {orders.map((o) => AwaitingPaymentRow({ order: o, t }))}
          </List>
          <BlockFooter>{ap.footer}</BlockFooter>
        </>
      )}
    </Page>
  );
}

function AwaitingPaymentRow({
  order,
  t,
}: {
  order: AwaitingPaymentOrder;
  t: ReturnType<typeof useT>;
}) {
  const ap = t.orders.awaitingPayment;
  return (
    <ListItem
      key={order.id}
      title={order.customerName}
      after={formatMoney(order.totalCents, order.currency)}
      subtitle={`#${order.confirmationCode} · ${t.orders.type[order.type]}`}
    >
      <i slot="media" className="f7-icons" style={{ fontSize: 24, color: '#8e8e93' }}>
        {paymentMethodIcon(order.paymentMethodKind)}
      </i>
      <div slot="text" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span>{paymentMethodLabel(order.paymentMethodKind, t.orders.methodKind)}</span>
        {order.customerPhone && (
          <a
            href={`tel:${order.customerPhone}`}
            className="external"
            aria-label={ap.call(order.customerName)}
          >
            {formatPhone(order.customerPhone)}
          </a>
        )}
        <span>
          {ap.expiresIn}{' '}
          {order.paymentExpiresAt ? (
            <PaymentCountdown expiresAt={order.paymentExpiresAt} />
          ) : (
            ap.noDeadline
          )}
        </span>
      </div>
    </ListItem>
  );
}
